import { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const PERFORMER_API = "https://api.muktihospital.com/api/pathology-performers";
const TEST_API = "https://api.muktihospital.com/api/pathology";

export default function PathologyAssignTestsModal({ performer, onClose, onSaved }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isOpen = !!performer;

  useEffect(() => {
    if (!performer) return;
    const load = async () => {
      setLoading(true);
      try {
        // 1) fetch all tests, keep only those matching this performer's type
        const testRes = await axios.get(TEST_API, {
          headers: { "x-api-key": API_KEY },
        });
        const allTests = Array.isArray(testRes.data)
          ? testRes.data
          : Array.isArray(testRes.data.data)
          ? testRes.data.data
          : [];
        const matching = allTests.filter(
          (t) => (t.performerType || "doctor") === performer.type
        );
        const opts = matching.map((t) => ({
          value: t.id,
          label: `${t.testName}${
            t.performerCommission ? ` — ৳${t.performerCommission}` : ""
          }`,
        }));
        setOptions(opts);

        // 2) fetch this performer's current mapping
        const mapRes = await axios.get(
          `${PERFORMER_API}/${performer.id}/tests`,
          { headers: { "x-api-key": API_KEY } }
        );
        const assignedIds = mapRes.data.testIds || [];
        setSelected(opts.filter((o) => assignedIds.includes(o.value)));
      } catch (err) {
        console.error("Load assign tests error:", err);
        toast.error("Failed to load tests.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [performer]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const safeSelected = Array.isArray(selected) ? selected : [];
      const testIdsToSend = safeSelected.map((o) => o.value);
      console.log("ASSIGN DEBUG → selected:", safeSelected);
      console.log("ASSIGN DEBUG → testIds being sent:", testIdsToSend);
      const putRes = await axios.put(
        `${PERFORMER_API}/${performer.id}/tests`,
        { testIds: testIdsToSend },
        { headers: { "x-api-key": API_KEY } }
      );
      console.log("ASSIGN DEBUG → server response:", putRes.data);
      toast.success("Tests assigned successfully.");
      if (onSaved) onSaved();
      else onClose();
    } catch (err) {
      console.error("Save assign tests error:", err);
      toast.error(err.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 print-hidden">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <h2 className="text-xl font-bold mb-1 text-gray-700">Assign Tests</h2>
        <p className="text-sm text-gray-500 mb-4">
          {performer.name}
          <span
            className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${
              performer.type === "technician"
                ? "bg-amber-50 text-amber-600 border border-amber-200"
                : "bg-blue-50 text-blue-600 border border-blue-200"
            }`}
          >
            {performer.type === "technician" ? "Technician" : "Doctor"}
          </span>
        </p>

        {loading ? (
          <div className="py-8 text-center text-gray-400 text-sm">
            Loading tests...
          </div>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tests this {performer.type} performs
            </label>
            <Select
              isMulti
              options={options}
              value={selected}
              onChange={(arr) => setSelected(Array.isArray(arr) ? arr : [])}
              isSearchable
              placeholder="Search and select tests..."
              classNamePrefix="rs"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
            <p className="text-xs text-gray-400 mt-2">
              Only {performer.type} tests are shown ({options.length} available).
            </p>
          </>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}